import { AccessTokenProvider } from '@sparx/grpc/lib/accessToken';
import { readCSRFCookie } from 'phoenix2/state/cookie/utils';
import { BrowserHeaders } from 'browser-headers';

export interface IFetchParams {
    method?: string;
    url: string;
    searchParams?: SearchParamsRecord;
    body?: any;
}

export type FetchFunction = <JSONType>(params: IFetchParams) => Promise<IResponse<JSONType>>;
export type SearchParamsRecord = Record<string, string | string[]>;

function searchParamsString(params: SearchParamsRecord): string {
    const builder = new URLSearchParams();
    for (const key of Object.keys(params)) {
        const val = params[key];
        if (typeof val === 'string') {
            builder.append(key, val);
        } else {
            for (const vval of val) {
                builder.append(key, vval);
            }
        }
    }
    return builder.toString();
}

export interface ISuccessResponse<JSONType> extends Response {
    readonly ok: true;

    json(): Promise<JSONType>;
}

export interface IFailureResponse extends Response {
    readonly ok: false;
}

export const VariantHeader = 'SPX-Variant-Selector';

export const getVariantFromURL = () => {
    const params = new URLSearchParams(window?.location.search);
    return params.get('spx.variant');
};

/**
 * A Response with a type for its json() function if it succeeded. Check if
 * the 'ok' field is true or false to distinguish success from failure.
 */
export type IResponse<JSONType> = ISuccessResponse<JSONType> | IFailureResponse;

export const fetcher = (
    baseUrl: string,
    getCsrfToken?: () => string,
    onStatus?: (status: number) => void,
) => <TJSONType>(params: IFetchParams): Promise<IResponse<TJSONType>> => {
    let url = baseUrl + params.url;
    if (params.searchParams) {
        url = url + '?' + searchParamsString(params.searchParams);
    }
    const headers = {
        'Content-Type': 'application/json; charset=utf-8',
    };
    // numerise sends csrf tokens in the query params rather than as a header
    if (getCsrfToken) {
        headers['X-CSRF-Token'] = getCsrfToken();
    }
    const v = getVariantFromURL();
    if (v !== null) {
        headers[VariantHeader] = v;
    }
    return fetch(url, {
        body: params.body && JSON.stringify(params.body),
        credentials: 'include',
        headers,
        method: params.method,
    }).then(resp => {
        if (onStatus !== undefined) {
            onStatus(resp.status);
        }
        return resp;
    });
};

/**
 * successJSONOrError returns the JSON body of the Response if it was
 * successful (200 <= status code <= 299), otherwise it throws an error
 * containing a status message corresponding to the Response's status code.
 */
export function successJSONOrError<TJSONType>(resp: IResponse<TJSONType>) {
    if (resp.ok) {
        return resp.json();
    }
    throw new Error(resp.statusText);
}

interface ISettings {
    swServerApiUrl: string;
}

declare global {
    // eslint-disable-next-line @typescript-eslint/interface-name-prefix
    interface Window {
        settings?: any;
    }
}
const settings: ISettings = window.settings;

export const getAccessTokenProvider = (
    accessTokenURL: string,
    onStatus?: (status: number) => void,
): AccessTokenProvider => {
    // We need the swserver URL to be able to get an access token, throw an error if this isn't set.
    if (!settings.swServerApiUrl) {
        throw new Error('Missing config: window.settings.swServerApiUrl');
    }

    // Wraps fetch with correct options.
    const myFetcher = fetcher(settings.swServerApiUrl, readCSRFCookie, onStatus);

    const accessTokenProvider = new AccessTokenProvider(() => myFetcher({ url: accessTokenURL }));

    accessTokenProvider.start().then(() => {
        // eslint-disable-next-line no-console
        console.log('Access Token Provider initialised');
    });

    return accessTokenProvider;
};

export const getAccessTokenMetadata = (accessTokenProvider: AccessTokenProvider) => async () => {
    const headers = {};

    const tok = await accessTokenProvider.requestAccessToken();
    /* tslint:disable:no-string-literal */
    headers['Authorization'] = tok;
    /* tslint:enable:no-string-literal */

    const v = getVariantFromURL();
    if (v !== null) {
        headers[VariantHeader] = v;
    }
    return new BrowserHeaders(headers);
};
